import { Trans } from "@lingui/react/macro"
import { Button, Group, List, Modal, Stack, Text } from "@mantine/core"
import type { IProps } from "./interfaces"

/**
 * Confirm Full → Lite (hide) or Lite → Full (restore or process). Never
 * implied by the avatar default-for-new-meetings menu.
 */
export const ConvertRecordingModeModal = ({
  opened,
  onClose,
  target,
  hasAnalysis,
  estimateCredits,
  loading,
  onConfirm,
}: IProps): React.JSX.Element => {
  const hide = target === "lite"
  const restore = target === "full" && hasAnalysis
  const upgrade = target === "full" && !hasAnalysis

  const title = hide ? (
    <Trans>Convert to Lite</Trans>
  ) : restore ? (
    <Trans>Restore Full workspace</Trans>
  ) : (
    <Trans>Upgrade to Full</Trans>
  )

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      centered
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <Stack gap="md">
        {hide ? (
          <>
            <Text size="sm">
              <Trans>
                This hides Full surfaces for this meeting. The summary is not deleted — you can restore Full later
                without reprocessing.
              </Trans>
            </Text>
            <Text size="sm" fw={600}>
              <Trans>These leave the workspace:</Trans>
            </Text>
            <List size="sm" spacing={4}>
              <List.Item>
                <Trans>Summary</Trans>
              </List.Item>
              <List.Item>
                <Trans>Decisions</Trans>
              </List.Item>
              <List.Item>
                <Trans>Action items</Trans>
              </List.Item>
              <List.Item>
                <Trans>Reports and translations</Trans>
              </List.Item>
              <List.Item>
                <Trans>Quality, sentiment, and talk-share cards</Trans>
              </List.Item>
            </List>
          </>
        ) : null}
        {restore ? (
          <Text size="sm">
            <Trans>
              This meeting already has a summary. Restore the Full workspace? Nothing needs to be reprocessed.
            </Trans>
          </Text>
        ) : null}
        {upgrade ? (
          <>
            <Text size="sm">
              <Trans>
                Lexicanon will process this recording and generate a summary, decisions, action items, and the rest of
                the Full workspace. That takes a few minutes and uses credits.
              </Trans>
            </Text>
            {estimateCredits != null && estimateCredits > 0 ? (
              <Text size="sm">
                <Trans>About {estimateCredits} credits.</Trans>
              </Text>
            ) : estimateCredits === null ? (
              <Text size="sm">
                <Trans>Working out the credit estimate…</Trans>
              </Text>
            ) : null}
            <Text size="sm">
              <Trans>
                The transcript stays as it is until processing finishes. If it fails, this meeting stays Lite.
              </Trans>
            </Text>
          </>
        ) : null}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            <Trans>Cancel</Trans>
          </Button>
          <Button onClick={onConfirm} loading={loading}>
            {upgrade ? (
              <Trans>Agree and start processing</Trans>
            ) : hide ? (
              <Trans>Convert to Lite</Trans>
            ) : (
              <Trans>Restore Full</Trans>
            )}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
